import { topicWall } from '@/mocks/topics';
import Reveal from '@/components/base/Reveal';

interface TopicWallProps {
  activeTag: string;
  onSelectTag: (tag: string) => void;
}

const GROUP_TONE: Record<string, { chip: string; dot: string }> = {
  event: {
    chip: 'border-primary-200 bg-primary-50 text-primary-700 hover:border-primary-400 hover:bg-primary-100',
    dot: 'bg-primary-500',
  },
  focus: {
    chip: 'border-accent-200 bg-accent-50 text-accent-800 hover:border-accent-400 hover:bg-accent-100',
    dot: 'bg-accent-500',
  },
  status: {
    chip: 'border-secondary-200 bg-secondary-50 text-secondary-800 hover:border-secondary-400 hover:bg-secondary-100',
    dot: 'bg-secondary-500',
  },
};

export default function TopicWall({ activeTag, onSelectTag }: TopicWallProps) {
  return (
    <section id="topics" className="w-full bg-background-50 px-4 py-16 md:px-8 md:py-20">
      <div className="mx-auto w-full max-w-7xl">
        <Reveal className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
          <div>
            <span className="font-label text-[11px] tracking-[0.24em] text-primary-600">
              TOPIC WALL / 话题墙
            </span>
            <h2 className="mt-3 font-heading text-2xl font-bold text-foreground-950 md:text-4xl">
              先用标签缩小范围，再决定看不看
            </h2>
          </div>
          <p className="max-w-md text-sm leading-relaxed text-foreground-600">
            深夜黑料负面条目按事件类型、争议焦点与状态标签拆开，点任意标签即可跳到底部速览，直接筛出对应条线。
          </p>
        </Reveal>

        <div className="mt-10 grid grid-cols-1 gap-6 lg:grid-cols-3">
          {topicWall.groups.map((group, groupIndex) => {
            const tone = GROUP_TONE[group.id] ?? GROUP_TONE.event;
            return (
              <Reveal key={group.id} delay={groupIndex * 90}>
                <div className="h-full rounded-lg border border-background-200 bg-background-100 p-5 md:p-6">
                  <div className="flex items-center justify-between">
                    <span className="flex items-center gap-2 font-heading text-base font-bold text-foreground-950">
                      <span className={`h-2 w-2 rounded-full ${tone.dot}`} />
                      {group.label}
                    </span>
                    <span className="font-label text-[11px] text-foreground-400">
                      {group.tags.length} 个
                    </span>
                  </div>
                  <p className="mt-2 text-xs leading-relaxed text-foreground-500">{group.hint}</p>

                  <div className="mt-5 flex flex-wrap gap-2">
                    {group.tags.map((tag) => {
                      const isActive = activeTag === tag.name;
                      return (
                        <button
                          key={tag.id}
                          type="button"
                          onClick={() => onSelectTag(tag.name)}
                          className={`flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${
                            isActive
                              ? 'border-primary-500 bg-primary-500 text-background-50 dark:text-foreground-950'
                              : tone.chip
                          }`}
                        >
                          {tag.name}
                          <span
                            className={`font-label text-[10px] ${
                              isActive ? 'text-background-50/80 dark:text-foreground-950/80' : 'opacity-60'
                            }`}
                          >
                            {tag.count}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}